/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Container, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import { StatusContext } from './StatusContext';
import { deleteFingerprint, Fingerprint, getPendingDeleteFingerprints } from '../data';

type DeleteState = {
    running: boolean;
    completed: number;
    total: number;
    currentPvkey: string;
};

function getYeetPendingValue(fp: Fingerprint): string {
    const resTags = (fp.res_tags && typeof fp.res_tags === 'object')
        ? fp.res_tags as Record<string, unknown>
        : {};
    const value = resTags.yeet_pending;
    if (value === true) {
        return 'true';
    }
    if (value === false) {
        return 'false';
    }
    if (value === null || value === undefined) {
        return '';
    }
    return String(value);
}

function getAnalystNote(fp: Fingerprint): string {
    const resTags = (fp.res_tags && typeof fp.res_tags === 'object')
        ? fp.res_tags as Record<string, unknown>
        : {};
    const note = resTags.note;
    return typeof note === 'string' ? note : '';
}

function PendingDeletesPage() {
    const statusContext = useContext(StatusContext);
    const [pendingFingerprints, setPendingFingerprints] = useState<Fingerprint[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string>('');
    const [deleteError, setDeleteError] = useState<string>('');
    const [deleteState, setDeleteState] = useState<DeleteState>({
        running: false,
        completed: 0,
        total: 0,
        currentPvkey: '',
    });

    const loadPendingDeletes = useCallback(async () => {
        setIsLoading(true);
        setLoadError('');
        try {
            const rows = await getPendingDeleteFingerprints();
            setPendingFingerprints(rows);
        } catch (error: any) {
            const message = error?.message || 'Failed to load pending deletes';
            setLoadError(message);
            statusContext.postError(`Failed to load pending deletes: ${message}`);
        } finally {
            setIsLoading(false);
        }
    }, [statusContext]);

    useEffect(() => {
        loadPendingDeletes();
    }, [loadPendingDeletes]);

    const progressNow = useMemo(() => {
        if (deleteState.total <= 0) {
            return 0;
        }
        return (deleteState.completed / deleteState.total) * 100;
    }, [deleteState.completed, deleteState.total]);

    async function handleDeleteFingerprints() {
        if (pendingFingerprints.length === 0 || deleteState.running) {
            return;
        }
        const confirmed = window.confirm(`Delete ${pendingFingerprints.length} pending fingerprint${pendingFingerprints.length === 1 ? '' : 's'}? This cannot be undone.`);
        if (!confirmed) {
            return;
        }

        setDeleteError('');
        setDeleteState({
            running: true,
            completed: 0,
            total: pendingFingerprints.length,
            currentPvkey: '',
        });

        try {
            for (let index = 0; index < pendingFingerprints.length; index += 1) {
                const fingerprint = pendingFingerprints[index];
                setDeleteState({
                    running: true,
                    completed: index,
                    total: pendingFingerprints.length,
                    currentPvkey: fingerprint.pvkey,
                });
                await deleteFingerprint(fingerprint.fid);
            }

            setDeleteState({
                running: false,
                completed: pendingFingerprints.length,
                total: pendingFingerprints.length,
                currentPvkey: '',
            });
            statusContext.postInfo(`Deleted ${pendingFingerprints.length} pending fingerprint${pendingFingerprints.length === 1 ? '' : 's'}.`);
            await loadPendingDeletes();
        } catch (error: any) {
            const message = error?.message || 'Failed to delete pending fingerprints';
            setDeleteError(message);
            setDeleteState((prev) => ({
                ...prev,
                running: false,
            }));
            statusContext.postError(`Pending delete failed: ${message}`);
            await loadPendingDeletes();
        }
    }

    return (
        <Container fluid className="py-3 px-4">
            <Row className="g-4 align-items-stretch">
                <Col lg={4}>
                    <Card border="warning" className="h-100">
                        <Card.Body className="d-flex flex-column justify-content-between">
                            <div>
                                <div className="text-uppercase text-muted small mb-2">Pending Deletes</div>
                                <div style={{ fontSize: '4rem', fontWeight: 700, lineHeight: 1 }}>
                                    {pendingFingerprints.length}
                                </div>
                                <div className="text-muted mt-2">
                                    fingerprint{pendingFingerprints.length === 1 ? '' : 's'} marked for deletion
                                </div>
                            </div>
                            <div className="mt-4 d-grid gap-3">
                                <Button
                                    size="lg"
                                    variant="danger"
                                    disabled={isLoading || deleteState.running || pendingFingerprints.length === 0}
                                    onClick={handleDeleteFingerprints}
                                >
                                    {deleteState.running ? 'Deleting fingerprints...' : 'Delete Fingerprints'}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    disabled={isLoading || deleteState.running}
                                    onClick={() => { void loadPendingDeletes(); }}
                                >
                                    Refresh
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={8}>
                    <Card className="h-100">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <h3 className="mb-1">Pending Fingerprint Deletes</h3>
                                    <div className="text-muted">Records with a res_tags.yeet_pending value.</div>
                                </div>
                                {isLoading ? <Spinner animation="border" size="sm" /> : null}
                            </div>

                            {loadError ? <Alert variant="danger">{loadError}</Alert> : null}
                            {deleteError ? <Alert variant="danger">{deleteError}</Alert> : null}

                            {deleteState.running || deleteState.completed === deleteState.total && deleteState.total > 0 ? (
                                <div className="mb-4">
                                    <ProgressBar
                                        now={progressNow}
                                        label={`${Math.round(progressNow)}%`}
                                        animated={deleteState.running}
                                        variant={deleteState.running ? 'warning' : 'success'}
                                    />
                                    <div className="d-flex justify-content-between mt-2 text-muted small">
                                        <span>{deleteState.completed} / {deleteState.total} deleted</span>
                                        <span>{deleteState.currentPvkey ? `Current: ${deleteState.currentPvkey}` : (deleteState.running ? 'Starting...' : 'Complete')}</span>
                                    </div>
                                </div>
                            ) : null}

                            <div style={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
                                <Table striped hover size="sm">
                                    <thead>
                                        <tr>
                                            <th>PVKey</th>
                                            <th>Product</th>
                                            <th>Vendor</th>
                                            <th>Yeet Pending</th>
                                            <th>Analyst Note</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!isLoading && pendingFingerprints.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="text-center text-muted py-4">No pending deletes.</td>
                                            </tr>
                                        ) : pendingFingerprints.map((fingerprint) => (
                                            <tr key={fingerprint.fid}>
                                                <td className="font-monospace">{fingerprint.pvkey}</td>
                                                <td>{fingerprint.product}</td>
                                                <td>{fingerprint.vendor}</td>
                                                <td>{getYeetPendingValue(fingerprint)}</td>
                                                <td>{getAnalystNote(fingerprint)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default PendingDeletesPage;